/**
 * Error Display Component
 * 
 * Displays error messages in a yellow warning box with exclamation icon
 * Used when cam_output is blank/not present
 */

import React from 'react';

/**
 * Error Display Component
 * 
 * @param {Object} props
 * @param {Array} props.errors - Array of error objects
 * @param {string} props.className - Additional CSS class
 * @param {Object} props.style - Additional inline styles
 */
const ErrorDisplay = ({ errors, className = '', style = {} }) => {
  // If no errors, don't render anything
  if (!errors || errors.length === 0) {
    return null;
  }

  // Filter out errors that don't have an error message
  const validErrors = errors.filter(err => err && err.error && err.error.trim() !== '');
  
  if (validErrors.length === 0) {
    return null;
  }

  // Container styles - yellow warning box
  const containerStyles = {
    backgroundColor: '#fef3c7', // yellow-100
    border: '1px solid #f59e0b', // yellow-500
    borderRadius: '8px',
    padding: '16px',
    margin: '16px 0',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    ...style
  };

  // Icon styles
  const iconStyles = {
    flexShrink: 0,
    width: '20px',
    height: '20px',
    color: '#d97706', // yellow-600
    marginTop: '2px'
  };

  // Content styles
  const contentStyles = {
    flex: 1
  };

  // Title styles
  const titleStyles = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#92400e', // yellow-800
    marginBottom: '8px'
  };

  // Error message styles
  const errorMessageStyles = {
    fontSize: '18px',
    color: '#78350f', // yellow-900
    lineHeight: 1.5,
    marginBottom: '8px'
  };

  // Last error message without margin
  const lastErrorMessageStyles = {
    ...errorMessageStyles,
    marginBottom: 0
  };

  return (
    <div className={`error-display-container ${className}`} style={containerStyles}>
      {/* Exclamation Icon */}
      <svg 
        style={iconStyles} 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
          clipRule="evenodd"
        />
      </svg>

      {/* Error Content */}
      <div style={contentStyles}>
        <div style={titleStyles}>
          Warning
        </div>
        {validErrors.map((err, index) => (
          <div 
            key={index}
            style={index === validErrors.length - 1 ? lastErrorMessageStyles : errorMessageStyles}
          >
            {err.error}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Check if data has errors that should be displayed
 * 
 * @param {Object} data - The data object to check
 * @returns {boolean} True if there are errors to display
 */
export const hasErrors = (data) => {
  if (!data) return false;
  
  // Check if cam_output is blank, empty, or not present
  // An empty object {} or empty string should be treated as blank
  const camOutput = data.cam_output;
  const hasCamOutput = camOutput && 
    (typeof camOutput === 'object' ? Object.keys(camOutput).length > 0 : String(camOutput).trim() !== '');
  
  // If cam_output is present and has content, no need to show errors
  if (hasCamOutput) return false;
  
  // Check if errors array exists and has items with error messages
  return data.errors && 
    Array.isArray(data.errors) && 
    data.errors.length > 0 &&
    data.errors.some(err => err && err.error && err.error.trim() !== '');
};

/**
 * Get errors from data
 * Only uses the errors array, not applicant_data.error
 * 
 * @param {Object} data - The data object
 * @returns {Array} Array of error objects
 */
export const getErrors = (data) => {
  if (!data || !data.errors || !Array.isArray(data.errors)) {
    return [];
  }
  
  // Only return errors from the errors array
  return data.errors.filter(err => err && err.error && err.error.trim() !== '');
};

/**
 * Check if an object is empty
 */
const isEmptyObject = (obj) => {
  if (!obj || typeof obj !== 'object') return true;
  return Object.keys(obj).length === 0;
};

export default ErrorDisplay;
